/**
 * SearchBox — buscador del header (ARS-21): número de regulador o nombre de
 * calle, todo sobre datos estáticos (`searchIndex.ts`). Sustituye a la
 * consola oculta "Ir a regulador" (tecla `i`), que nadie descubría; la tecla
 * `i` ahora enfoca este cuadro (señal vía `useCommandConsole.open`).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, MapPin, Route } from 'lucide-react';
import { useCommandConsole } from '../store/commandConsole';
import { search, type SearchHit } from '../utils/searchIndex';
import { flyToRegulator, flyToStreet } from '../utils/camera-api';

export function SearchBox() {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const focusSignal = useCommandConsole(s => s.open);
  const closeSignal = useCommandConsole(s => s.close);

  const hits = useMemo(() => (query.trim() ? search(query) : []), [query]);

  // Tecla `i` (listener global en SantaColomaMapView) → enfocar el cuadro.
  useEffect(() => {
    if (!focusSignal) return;
    inputRef.current?.focus();
    inputRef.current?.select();
    closeSignal();
  }, [focusSignal, closeSignal]);

  useEffect(() => { setActive(0); }, [hits]);

  const go = (hit: SearchHit) => {
    if (hit.kind === 'regulator') flyToRegulator(hit.intersection);
    else flyToStreet(hit.street);
    setOpen(false);
    setQuery('');
    inputRef.current?.blur();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
      inputRef.current?.blur();
    } else if (e.key === 'ArrowDown' && hits.length) {
      e.preventDefault();
      setActive(a => (a + 1) % hits.length);
    } else if (e.key === 'ArrowUp' && hits.length) {
      e.preventDefault();
      setActive(a => (a - 1 + hits.length) % hits.length);
    } else if (e.key === 'Enter' && hits[active]) {
      e.preventDefault();
      go(hits[active]);
    }
  };

  const showList = open && query.trim().length > 0;

  return (
    <div className="relative">
      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-gray-800/60 rounded-lg border border-gray-700/50 focus-within:border-cyan-600/60">
        <Search className="w-3 h-3 text-gray-500 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
          onKeyDown={onKeyDown}
          placeholder="Regulador o calle…"
          title="Buscar regulador o calle (tecla i)"
          className="bg-transparent text-[11px] text-gray-200 placeholder:text-gray-600 outline-none w-24 sm:w-44 lg:w-56 font-mono"
        />
      </div>
      {showList && (
        <div className="absolute left-0 top-full mt-1 w-72 max-h-80 overflow-y-auto bg-gray-900/95 backdrop-blur-md border border-gray-700/60 rounded-lg shadow-2xl z-50">
          {hits.length === 0 ? (
            <div className="px-3 py-2 text-[11px] text-gray-500">Sin resultados</div>
          ) : (
            hits.map((hit, i) => {
              const isActive = i === active;
              const key = hit.kind === 'regulator' ? `r-${hit.intersection.cabinet.logicalId}` : `s-${hit.street.name}`;
              return (
                <button
                  key={key}
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => go(hit)}
                  onMouseEnter={() => setActive(i)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11px] transition-colors ${isActive ? 'bg-cyan-900/40 text-white' : 'text-gray-300 hover:bg-gray-800/60'}`}
                >
                  {hit.kind === 'regulator' ? (
                    <>
                      <MapPin className="w-3 h-3 text-cyan-400 flex-shrink-0" />
                      <span className="font-mono font-medium text-cyan-300">{hit.displayId}</span>
                      <span className="truncate text-gray-400">{hit.intersection.name}</span>
                    </>
                  ) : (
                    <>
                      <Route className="w-3 h-3 text-amber-400 flex-shrink-0" />
                      <span className="truncate">{hit.street.name}</span>
                    </>
                  )}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
